/**
 * Session-continuity helpers around the MSAL redirect flow.
 *
 * When an access token can't be silently renewed (the SPA refresh token hit its
 * 24h cap, Conditional Access re-challenged, or the sessionStorage cache was
 * evicted), MSAL must send the user through an interactive sign-in — a full-page
 * redirect that reboots the app and throws away all in-memory view state.
 *
 * This module softens that in three ways:
 *  1. Return-to breadcrumb — the screen the user was on (and any open report id)
 *     is stashed in localStorage *before* the redirect and restored on the next
 *     boot, so they land back where they were instead of on the default screen.
 *     Only non-sensitive navigation state is stored (never tokens), and it is
 *     keyed to the MSAL account so a *different* user signing in on a shared
 *     device never inherits it.
 *  2. Reauth prompt — instead of an abrupt bounce to Microsoft, callers get a
 *     chance to show a "your session ended, your work is saved" modal and only
 *     redirect on the user's tap (see onReauthRequired / redirectToLogin).
 *  3. Focus refresh — a silent token renewal when the tab regains focus, so the
 *     next user action is less likely to be the thing that triggers the redirect
 *     (see startTokenRefreshOnFocus).
 */
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, apiTokenRequest } from './msalConfig';

/** True when the device reports no connectivity. Conservative: unknown = online. */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * MSAL BrowserAuthError codes that genuinely mean "silent renewal can never
 * succeed here — the user has to sign in interactively". This is deliberately an
 * ALLOWLIST, because MSAL reuses BrowserAuthError for plain network failures:
 * its FetchClient throws `no_network_connectivity` when navigator.onLine is
 * false and `post_request_failed` when a fetch dies while onLine is true (flaky
 * cellular — the normal case for a field iPad). Treating those as a dead session
 * would send a tech mid-inspection to login.microsoftonline.com, which cannot
 * load without connectivity, leaving the app gone from the tab. Anything not
 * listed here rethrows, exactly as it did before session continuity existed.
 */
const INTERACTION_REQUIRED_BROWSER_CODES = new Set<string>([
  'monitor_window_timeout',    // hidden silent-renewal iframe never returned (Safari/ITP)
  'iframe_closed_prematurely', // iframe torn down before the token came back
  'block_iframe_reload',       // redirect inside the iframe was blocked
  'redirect_in_iframe',        // silent flow tried a redirect it isn't allowed to do
]);

/**
 * Should this token-acquisition failure send the user through interactive
 * sign-in? Exported so the offline/network classification is unit-testable
 * without standing up MSAL.
 */
export function isInteractionRequiredError(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  if (err instanceof BrowserAuthError) return INTERACTION_REQUIRED_BROWSER_CODES.has(err.errorCode);
  return false;
}

const RETURN_KEY = 'app.return_location';
/** A breadcrumb older than this is ignored — the user has moved on. */
const MAX_AGE_MS = 5 * 60 * 1000;

export interface ReturnLocation {
  view: string;
  activeReportId: string | null;
}

interface StoredReturnLocation extends ReturnLocation {
  /** MSAL homeAccountId of the user who left — guards shared devices. */
  acct: string;
  ts: number;
}

/** The MSAL account currently signed in on this tab, if any. */
function activeHomeAccountId(): string | null {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  return account?.homeAccountId ?? null;
}

// --- 1. Return-to breadcrumb ------------------------------------------------

// App keeps this current so the redirect path (which runs deep in the API layer,
// with no access to React state) can snapshot where the user is.
let currentLocation: ReturnLocation = { view: '', activeReportId: null };

/** Called by the app whenever the visible screen changes. */
export function setCurrentLocation(view: string, activeReportId: string | null): void {
  currentLocation = { view, activeReportId };
}

/** Persist the current screen so we can return to it after re-auth. Best-effort. */
export function saveReturnLocation(): void {
  try {
    const acct = activeHomeAccountId();
    if (!acct || !currentLocation.view) return;
    const payload: StoredReturnLocation = {
      view: currentLocation.view,
      activeReportId: currentLocation.activeReportId,
      acct,
      ts: Date.now(),
    };
    localStorage.setItem(RETURN_KEY, JSON.stringify(payload));
  } catch {
    /* localStorage unavailable (private mode, quota) — best-effort only */
  }
}

/**
 * Read the saved breadcrumb without clearing it (pure — safe to call from a
 * render/useState initializer). Returns null when absent, stale, or belonging to
 * a different account than the one now signed in.
 */
export function peekReturnLocation(): ReturnLocation | null {
  try {
    const raw = localStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as StoredReturnLocation;
    if (!p || typeof p.view !== 'string') return null;
    if (p.acct !== activeHomeAccountId()) return null; // different user on a shared device
    if (Date.now() - p.ts > MAX_AGE_MS) return null; // user has moved on
    return { view: p.view, activeReportId: p.activeReportId ?? null };
  } catch {
    return null;
  }
}

/** Drop the breadcrumb once it's been consumed. */
export function clearReturnLocation(): void {
  try {
    localStorage.removeItem(RETURN_KEY);
  } catch {
    /* best-effort */
  }
}

// --- 2. Reauth prompt -------------------------------------------------------

type ReauthHandler = () => void;
let reauthHandler: ReauthHandler | null = null;
let reauthPending = false;

/**
 * Register the UI that reacts to "interactive sign-in required" (e.g. show a
 * modal). Returns an unsubscribe. If no handler is registered when reauth is
 * needed, we redirect immediately rather than leaving the user stuck.
 */
export function onReauthRequired(cb: ReauthHandler): () => void {
  reauthHandler = cb;
  return () => {
    if (reauthHandler === cb) reauthHandler = null;
  };
}

/**
 * Called from the API layer when a silent token renewal failed with an
 * interaction-required error. Saves the return breadcrumb, then hands off to the
 * registered UI (or redirects straight away if there is none). De-duped so a
 * burst of failing requests only triggers one prompt.
 */
export function requestReauth(): void {
  if (reauthPending) return;
  // Never drag an offline user toward sign-in. The redirect targets
  // login.microsoftonline.com, which can't load without connectivity, so the tab
  // would land on a browser error page and the app would be gone. Their work is
  // already safe in IndexedDB; if the session really is dead, the first request
  // made once connectivity returns re-triggers this.
  if (isOffline()) return;
  reauthPending = true;
  saveReturnLocation();
  if (reauthHandler) {
    reauthHandler();
  } else {
    void redirectToLogin();
  }
}

/**
 * Drop the one-prompt-at-a-time latch. Called when a prompt is dismissed without
 * actually signing in (offline), so a later failure can prompt again instead of
 * the app going permanently quiet about a dead session.
 */
export function releaseReauth(): void {
  reauthPending = false;
}

/** Send the user through an interactive sign-in, preserving their return spot. */
export async function redirectToLogin(): Promise<boolean> {
  // Same offline rule as requestReauth, re-checked here because connectivity can
  // drop between the prompt appearing and the user tapping it. Returning false
  // leaves the prompt on screen rather than navigating to a page that can't load.
  if (isOffline()) return false;
  saveReturnLocation();
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? undefined;
  await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
  return true;
}

// --- 3. Focus refresh -------------------------------------------------------

/**
 * Silently renew the access token whenever the tab regains focus, so the next
 * user action is less likely to be what triggers a redirect. Interaction-required
 * errors are swallowed here on purpose — we never bounce the user to sign-in just
 * for focusing the tab; that only happens on a real API call (via requestReauth).
 * Returns a cleanup that removes the listener.
 */
const FOCUS_REFRESH_MIN_INTERVAL_MS = 60_000;
let lastFocusRefresh = 0;

export function startTokenRefreshOnFocus(): () => void {
  const handler = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    // Field techs bounce to the Camera app constantly, so this fires a lot.
    // Offline it's a guaranteed-failed network call; throttled otherwise.
    if (isOffline()) return;
    const now = Date.now();
    if (now - lastFocusRefresh < FOCUS_REFRESH_MIN_INTERVAL_MS) return;
    lastFocusRefresh = now;
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) return;
    void msalInstance.acquireTokenSilent({ ...apiTokenRequest, account }).catch(() => {
      /* renew on demand later; do not force an interactive redirect on focus */
    });
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}
